import { debounce } from "lodash";
import {
  type MutableRefObject,
  type RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";

import {
  applyArrowVisibility,
  applyPlanningActivity,
  clearPlanningActivity,
  handleSetup,
  zoomToFit
} from "./PreviewCanvas.utils";
import { Exporter } from "../../Exporter";
import type { FieldData as DocumentFieldData } from "../DocumentExport/DocumentExportContent";
import type { FieldData as ImageFieldData } from "../ImageExport/ImageExportContent";
import { GetDoc, ReRenderCanvas } from "@/Utils/StateLib";
import { isNullish, useEventListener } from "@theorgchartdev/storybook";
import { StatusController } from "@/hooks/useStatusController";
import { useTopic } from "@/hooks/useTopic";
import { QSubChart } from "@coreapp/CoreApp/QSubChart";
import { setupCanvas } from "@coreapp/utils/setupCanvas";
import { handleMouseUp } from "@coreapp/utils/handleMouseUp";
import { handleMouseMove } from "@coreapp/utils/handleMouseMove";
import { handleMouseDown } from "@coreapp/utils/handleMouseDown";
import { handleMouseEnter } from "@coreapp/utils/handleMouseEnter";
import { handleDoubleClick } from "@coreapp/utils/handleDoubleClick";
import { QBackgroundPanel } from "@coreapp/CoreApp/QBackgroundPanel";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";

export const ScopeOptions = ["Book", "Branch", "Page"] as const;
export type Scope = (typeof ScopeOptions)[number];
export type ScopeController = {
  activeScope: Scope /* The value that is currently requested to be built */;
  failedScopes: Scope[];
  reduceScopeFlag: boolean;
  // onScopeChange is called if the user requests a new scope
  // If it returns true, if the mock document needs to be rebuilt
  onScopeChange: (newScope: Scope) => boolean;
};

type Options = {
  fieldData: DocumentFieldData | ImageFieldData;
  backgroundRef: MutableRefObject<{ color: string; fillMode: number }>;
  mockDocumentRef: MutableRefObject<QXDoc | null>;
  sourceDocumentRef: MutableRefObject<QXDoc | null>;
  exportStartedRef: MutableRefObject<boolean>;
  statusController: StatusController;
  scopeControllerRef?: RefObject<ScopeController>;
  // onReduceScope is called when the scope is reduced due to a failed build
  onReduceScope?: (scope: Scope) => void;
  onPostBuild?: (mockDocument: QXDoc) => void;
};

export const usePreviewDocument = (options: Options) => {
  const canvasParentRef = useRef<Nullable<HTMLDivElement>>(null);
  const [canvasEl, setCanvasEl] = useState<Nullable<HTMLCanvasElement>>(null);
  const canvasRef = useRef<Nullable<HTMLCanvasElement>>(null);

  const buildAbortController = useRef(new AbortController());
  const scopeControllerRef = useRef<ScopeController>({
    activeScope: getInitialActiveScope(options.fieldData, options.sourceDocumentRef.current),
    failedScopes: [],
    reduceScopeFlag: false,
    onScopeChange: handleScopeChange
  });
  useImperativeHandle(options.scopeControllerRef, () => scopeControllerRef.current);

  const teardownRef = useRef<Nullable<() => void>>(null);

  const fieldDataRef = useRef(options.fieldData);
  fieldDataRef.current = options.fieldData;

  const attemptBuild = async () => {
    options.statusController.setLoading();

    const canvasElement = canvasRef.current;
    const parentEl = canvasParentRef.current;
    const sourceDocument = options.sourceDocumentRef.current;

    if (isNullish(canvasElement) || isNullish(parentEl) || isNullish(sourceDocument)) {
      return;
    }

    if (scopeControllerRef.current.failedScopes.includes(scopeControllerRef.current.activeScope)) {
      teardownRef.current = null;
      options.statusController.setError();
      return;
    }

    buildAbortController.current = new AbortController();

    const handlePostTeardown = () => {
      if (!scopeControllerRef.current.reduceScopeFlag) {
        return;
      }

      scopeControllerRef.current.reduceScopeFlag = false;
      const sourceDocument = options.sourceDocumentRef.current;
      const prevScope = ScopeOptions.indexOf(scopeControllerRef.current.activeScope);
      scopeControllerRef.current.failedScopes.push(scopeControllerRef.current.activeScope);

      let nextScope = ScopeOptions[prevScope + 1] ?? "Page";

      // If we requsted a Page scope and it failed, ensure that the Branch scope is
      // distinct. If it is not, we can skip the Branch scope and go straight to Page
      if (prevScope === 0 && areBranchAndBookScopesEqual(sourceDocument)) {
        nextScope = "Page";
        scopeControllerRef.current.failedScopes.push("Branch");
      }

      options.onReduceScope?.(nextScope);
      scopeControllerRef.current.activeScope = nextScope;
      attemptBuild();
    };

    const handlePostBuild = (mockDocument: QXDoc) => {
      options.onPostBuild?.(mockDocument);
      options.statusController.setReady();
    };

    const teardown = await buildPreviewDocument({
      abortController: buildAbortController.current,
      onPostBuild: handlePostBuild,
      onPostTeardown: handlePostTeardown,
      backgroundRef: options.backgroundRef,
      scopeControllerRef,
      fieldDataRef,
      canvasEl: canvasElement,
      parentEl,
      mockDocumentRef: options.mockDocumentRef,
      sourceDocument
    });

    teardownRef.current = teardown;
  };

  function handleScopeChange(newScope: Scope) {
    const sourceDocument = options.sourceDocumentRef.current;
    const scopeComparison = compareScope(scopeControllerRef.current.activeScope, newScope);

    if (scopeComparison <= 0) {
      const hasChainLevels = sourceDocument?.GetRequestedChainLevels() > 0;
      if (!hasChainLevels) {
        return false;
      }

      if (scopeComparison === 0 || newScope !== "Page") {
        return false;
      }
    }

    if (newScope === "Branch" && areBranchAndBookScopesEqual(options.sourceDocumentRef.current)) {
      newScope = "Book";
    }

    teardownRef.current?.();
    scopeControllerRef.current.activeScope = newScope;
    attemptBuild();
    return true;
  }

  useTopic("TooManyResults", () => {
    scopeControllerRef.current.reduceScopeFlag = true;
    buildAbortController.current.abort();
  });

  useEffect(() => {
    const sourceDocument = GetDoc();
    const wsMgr = sourceDocument?.GetWSMgr();
    if (!wsMgr) {
      return;
    }

    options.sourceDocumentRef.current = sourceDocument;
    if (options.scopeControllerRef?.current && sourceDocument) {
      options.scopeControllerRef.current.activeScope = getInitialActiveScope(
        options.fieldData,
        sourceDocument
      );
    }
    canvasRef.current = canvasEl;

    wsMgr.SetSuppressProgress(true);
    attemptBuild();

    return () => {
      buildAbortController.current.abort();
      teardownRef.current?.();
      wsMgr?.SetSuppressProgress(false);
    };
  }, [canvasEl]);

  useTopic("previewSubchartSwitch", ([oldSc, newSc]: [QSubChart, QSubChart]) => {
    const mockDocument = options.mockDocumentRef.current;
    const sourceDocument = options.sourceDocumentRef.current;
    if (isNullish(mockDocument) || isNullish(sourceDocument) || options.exportStartedRef.current) {
      return;
    }

    const oldScId = oldSc?.GetSubChartID();
    const newScId = newSc?.GetSubChartID();

    if (oldScId === newScId) {
      return;
    }

    zoomToFit(mockDocument);
    applyPlanningActivity(mockDocument, fieldDataRef.current);
    applyArrowVisibility(mockDocument, sourceDocument, fieldDataRef.current);
  });

  const debouncedResize = debounce(() => {
    const mockDoc = options.mockDocumentRef.current;
    const parentRef = canvasParentRef.current;
    const canvasPanel = mockDoc?.GetCanvasRef();
    if (
      isNullish(parentRef) ||
      isNullish(canvasEl) ||
      isNullish(mockDoc) ||
      isNullish(canvasPanel)
    ) {
      return;
    }

    const backgroundPanel = canvasPanel.fBackgroundPanel;
    const boundingRect = parentRef.getBoundingClientRect();
    setupCanvas(canvasEl, boundingRect);
    const cWidth = Math.floor(boundingRect.width);
    const cHeight = Math.floor(boundingRect.height);
    canvasPanel.SizePanel(cWidth, cHeight);
    canvasPanel.PositionPanel(0, 0);
    backgroundPanel.SizePanel(cWidth, cHeight);
    canvasPanel.DoCustomResize();
    zoomToFit(mockDoc);
    ReRenderCanvas();
  }, 200);

  useEventListener("resize", debouncedResize);

  return {
    rebuildPreviewDocument: attemptBuild,
    setCanvasEl,
    canvasParentRef
  };
};

type BuildOptions = {
  abortController: AbortController;
  onPostBuild?: (mockDocument: QXDoc) => void;
  onPostTeardown: () => void;

  canvasEl: HTMLCanvasElement;
  parentEl: HTMLDivElement;

  backgroundRef: Options["backgroundRef"];
  scopeControllerRef: MutableRefObject<ScopeController>;
  fieldDataRef: MutableRefObject<Options["fieldData"]>;

  mockDocumentRef: Options["mockDocumentRef"];
  sourceDocument: QXDoc;
};

const buildPreviewDocument = async (options: BuildOptions) => {
  options.mockDocumentRef.current = null;

  const teardownFns: Array<() => void> = [];
  const abortController = options.abortController;

  const handleTeardown = () => {
    for (const teardown of teardownFns) {
      teardown();
    }

    options.onPostTeardown();
  };

  const exporter = new Exporter();
  const scopeOptions = getScopeOptions(
    options.scopeControllerRef.current.activeScope,
    options.sourceDocument
  );

  exporter
    .createMockDocument(options.sourceDocument, abortController, scopeOptions)
    .then(async ([mockDoc, teardownDocument]) => {
      teardownFns.unshift(teardownDocument);

      // Prevent async actions from updating state if the component has been unmounted
      if (abortController.signal.aborted || !mockDoc) {
        handleTeardown();
        return;
      }

      options.mockDocumentRef.current = mockDoc;

      const teardownCanvas = configureCanvas(options.parentEl, options.canvasEl, mockDoc);
      teardownFns.unshift(teardownCanvas);

      await handleSetup(
        mockDoc,
        options.sourceDocument,
        options.backgroundRef,
        options.fieldDataRef.current
      );

      if (abortController.signal.aborted) {
        handleTeardown();
        return;
      }

      options.onPostBuild?.(mockDoc);

      teardownFns.unshift(() => {
        clearPlanningActivity(mockDoc);
      });
    });

  return handleTeardown;
};

export const getScopeOptions = (scope: Scope, sourceDocument: QXDoc) => {
  const sourceScm = sourceDocument.GetSubChartMgr();
  const currentTopPersonId = sourceScm?.GetCurrentTopObjPersonID();

  switch (scope) {
    case "Book":
      const wsMgr = sourceDocument.GetWSMgr();
      const planMgr = wsMgr?.GetPlanMgr();
      const curRootSc = sourceScm?.GetRootSubChart();
      let topChartDbObj = curRootSc?.GetTopDBObj();
      if (planMgr) {
        const planTopObj = planMgr.GetTopObjRef();
        if (planTopObj) {
          topChartDbObj = planTopObj;
        }
      }

      return {
        topBoxID: topChartDbObj?.GetObjectRecordID(),
        levels: -1
      };
    case "Branch":
      return {
        topBoxID: currentTopPersonId,
        levels: -1
      };
    case "Page":
      let forceTopBoxID: Optional<string>;
      const chainLevels = sourceDocument.GetRequestedChainLevels();
      if (chainLevels > 0) {
        const sourceSc = sourceScm?.GetCurrentSubChart();
        let targetNode = sourceSc.GetTopDBObj();

        for (let i = 0; i < chainLevels; i++) {
          let targetParent = targetNode?.GetParent();
          if (isNullish(targetParent)) {
            break;
          }

          targetNode = targetParent;
        }

        forceTopBoxID = targetNode?.GetObjectRecordID();
      }

      // Non-plan: -1 (JSON clone loads the entire tree anyway).
      // Plan: OpenPlan hangs on -1, so cap at max(sourceLevels, 3) — enough for
      // grouping's parentIDSet to detect direct-report managers via grandchildren.
      const sourceLevels = sourceDocument.GetRequestedLevels();
      const pageLevels = sourceDocument.IsPlanChart()
        ? Math.max(sourceLevels > 0 ? sourceLevels : 3, 3)
        : -1;
      return {
        forceTopBoxID,
        topBoxID: currentTopPersonId,
        levels: pageLevels,
        chainLevels: sourceDocument.GetRequestedChainLevels()
      };
  }
};

const configureCanvas = (
  parent: Nullish<HTMLDivElement>,
  canvas: HTMLCanvasElement,
  doc: Nullish<QXDoc>
) => {
  const wsMgr = doc?.GetWSMgr();
  const canvasPanel = doc?.GetCanvasRef();
  if (
    isNullish(canvas) ||
    isNullish(doc) ||
    isNullish(parent) ||
    isNullish(wsMgr) ||
    isNullish(canvasPanel)
  ) {
    return () => {};
  }

  const boundingRect = parent.getBoundingClientRect();
  setupCanvas(canvas, boundingRect);

  (canvas as any)["MyApp"] = (window as any)["MyApp"];
  const onMouseMove = handleMouseMove(canvas);
  canvas.addEventListener("mousemove", onMouseMove, false);
  const onMouseDown = handleMouseDown(canvas);
  canvas.addEventListener("mousedown", onMouseDown, false);
  const onMouseUp = handleMouseUp(canvas);
  canvas.addEventListener("mouseup", onMouseUp, false);
  const onMouseEnter = handleMouseEnter(canvas);
  canvas.addEventListener("mouseenter", onMouseEnter, false);
  const onDoubleClick = handleDoubleClick(canvas);
  canvas.addEventListener("dblclick", onDoubleClick, false);
  // No need for right click handlers in preview
  const onContextMenu = (e: MouseEvent) => e.preventDefault();
  canvas.addEventListener("contextmenu", onContextMenu, false);

  const cWidth = Math.floor(boundingRect.width);
  const cHeight = Math.floor(boundingRect.height);

  canvasPanel.SetBackBuffer(canvas);
  canvasPanel.SizePanel(cWidth, cHeight);
  canvasPanel.PositionPanel(0, 0);

  const backgroundPanel = new QBackgroundPanel();
  backgroundPanel.InitBackgroundPanel(wsMgr);
  backgroundPanel.InitScrollBars(canvasPanel);
  backgroundPanel.PositionPanel(0, 0);
  backgroundPanel.SizePanel(cWidth, cHeight);
  backgroundPanel.SetResizeBehavior(true, true, true, true);
  backgroundPanel.SetFillColor("#EEEFF3");
  canvasPanel.fBackgroundPanel = backgroundPanel;

  canvasPanel.DoCustomResize();

  wsMgr.PushPanel(backgroundPanel);
  backgroundPanel.SetBackBuffer(canvas);

  backgroundPanel.RenderPanel();
  canvasPanel.RenderPanel();

  return () => {
    wsMgr.RemovePanel(backgroundPanel);

    canvas.removeEventListener("mousemove", onMouseMove);
    canvas.removeEventListener("mousedown", onMouseDown);
    canvas.removeEventListener("mouseup", onMouseUp);
    canvas.removeEventListener("mouseenter", onMouseEnter);
    canvas.removeEventListener("dblclick", onDoubleClick);
    canvas.removeEventListener("contextmenu", onContextMenu);
  };
};

const getInitialActiveScope = (
  fieldData: Options["fieldData"],
  sourceDocument: Nullable<QXDoc>
): Scope => {
  if (!("scope" in fieldData)) {
    // Image Exports only support Page scope
    return "Page";
  }

  if (fieldData.scope === "Branch" && areBranchAndBookScopesEqual(sourceDocument)) {
    return "Book";
  }

  return fieldData.scope;
};

export const compareScope = (a: Scope, b: Scope) => {
  return ScopeOptions.indexOf(a) - ScopeOptions.indexOf(b);
};

const areBranchAndBookScopesEqual = (sourceDocument: Nullable<QXDoc>) => {
  if (!sourceDocument) {
    return false;
  }

  const bookOptions = getScopeOptions("Book", sourceDocument);
  const branchOptions = getScopeOptions("Branch", sourceDocument);

  return bookOptions.topBoxID === branchOptions.topBoxID;
};
